import { estimateMonthlyItemBlockWidth } from './estimateMonthlyInlineItemWidth';
import { placeItemsInTracksByDateGroup } from './placeItemsInTracks';
import { sortMonthlyTrackPlacementItems } from './monthlyTrackPlacementSort';
import { resolveItemDayPosition } from './resolveItemDayPosition';
import type { MonthDescriptor, MonthlyTimelineItem } from './types';
import type { MonthlyResolvedDayPosition } from './resolveItemDayPosition';

export interface MonthlyTrackedPositionBase {
  leftPx: number;
  widthPx: number;
  dateGranularity: 'day' | 'month' | 'quarter';
  chronologyMs: number;
  item: MonthlyTimelineItem;
}

export interface MonthlyPlacementFields {
  widthPx: number;
  heightPx?: number;
}

interface BuildMonthlyTrackedPositionsOptions<
  T extends MonthlyTrackedPositionBase
> {
  items: MonthlyTimelineItem[];
  months: MonthDescriptor[];
  getDayLeftPx: (monthKey: string, day: number) => number | null;
  resolvePlacement?: (
    item: MonthlyTimelineItem,
    resolvedPosition: MonthlyResolvedDayPosition,
    leftPx: number
  ) => MonthlyPlacementFields | null;
  laneLabel?: string;
}

export function buildMonthlyTrackedPositions<
  T extends MonthlyTrackedPositionBase
>({
  items,
  months,
  getDayLeftPx,
  resolvePlacement,
  laneLabel = ''
}: BuildMonthlyTrackedPositionsOptions<T>): Array<
  T & { rowIndex: number; topPx: number }
> {
  const rawPositions = items
    .map(item => {
      const resolvedPosition = resolveItemDayPosition(item, months);
      if (!resolvedPosition) return null;

      const startLeftPx = getDayLeftPx(
        resolvedPosition.startMonthKey,
        resolvedPosition.startDay
      );
      const endLeftPx = getDayLeftPx(
        resolvedPosition.endMonthKey,
        resolvedPosition.endDay
      );

      if (startLeftPx === null || endLeftPx === null) return null;

      const leftPx = Math.min(startLeftPx, endLeftPx);
      const placement = resolvePlacement
        ? resolvePlacement(item, resolvedPosition, leftPx)
        : {
            widthPx: estimateMonthlyItemBlockWidth(item, laneLabel)
          };

      if (!placement) return null;

      return {
        leftPx,
        dateGranularity: resolvedPosition.dateGranularity,
        chronologyMs: resolvedPosition.chronologyMs,
        item,
        ...placement
      } as T;
    })
    .filter(Boolean) as T[];

  const sorted = sortMonthlyTrackPlacementItems(rawPositions);
  return placeItemsInTracksByDateGroup(sorted);
}
